import { useMemo, useState } from 'react';
import type { SingleOption } from '~/types';
import type { LocationOption } from './query-location';

interface LocationCardProps {
  option: SingleOption;
  defaultChecked: boolean;
  onChange(a: 'add' | 'remove', v: SingleOption): void;
  hasError: boolean;
}

export const LocationCard = (props: LocationCardProps): JSX.Element => {
  const { option, onChange, defaultChecked, hasError } = props;
  const { label } = option;
  const [isChecked, setChecked] = useState(defaultChecked);

  function handleChange(value: LocationOption) {
    if (isChecked) {
      setChecked(false);
      onChange('remove', value);
    } else {
      setChecked(true);
      onChange('add', value);
    }
  }

  const initials = useMemo(() => {
    return label
      .split(' ')
      .map(word => word[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }, [label]);

  const borderClass = useMemo(() => {
    if (hasError && isChecked) {
      return 'border-error-container';
    }
    if (isChecked) {
      return 'step-active border-slate-200 dark:border-outline-variant';
    }
    return 'border-slate-200 dark:border-outline-variant';
  }, [hasError, isChecked]);

  return (
    <div
      className={`bg-white dark:bg-surface-container-low border p-3 md:p-gutter rounded-lg transition-all hover:border-primary group cursor-pointer active:scale-[0.99] w-full ${borderClass}`}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        handleChange(option);
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 md:gap-gutter">
          <div className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary font-bold text-[14px] md:text-[18px] flex-shrink-0">
            {initials}
          </div>
          <div>
            <h3 className="font-headline-sm md:font-headline-md text-[15px] md:text-headline-md text-slate-800 dark:text-on-surface group-hover:text-primary transition-colors leading-tight">
              {label}
            </h3>
            {option?.data?.description && (
              <p className="text-slate-500 dark:text-on-surface-variant text-[12px] md:text-body-sm font-code-sm leading-normal mt-0.5">
                {option.data.description as string}
              </p>
            )}
          </div>
        </div>
        <span className="material-symbols-outlined text-slate-400 dark:text-on-surface-variant group-hover:text-primary text-[20px] md:text-[24px]">
          {isChecked ? 'check_circle' : 'chevron_right'}
        </span>
      </div>
    </div>
  );
};
